/**
 * Permission Calculation Logic
 * 
 * Pure functions to calculate user permissions based on user data, trials, and apps.
 * This is the single source of truth for all permission logic.
 */

import {
    UserPermissions,
    TrialStatus,
    AppPermission,
    TrialRecord,
    AppRecord,
    PromoData,
} from './types';

// User type from PocketBase
type User = any;

/**
 * Calculate the effective tier for a user.
 *
 * Mirrors the date-first waterfall in lib/auth-validation.ts::evaluateSubscription():
 *   1. Admin/super → tier 3
 *   2. No subscription data (tier=0, level=0) → 0
 *   3. No end date but has tier → manual/admin grant → return tier
 *   4. Date-first check: now ≤ subscription_end + 3-day grace → return tier
 *   5. Expired → 0
 *
 * NOTE: The static subscription_status field is intentionally ignored so that
 * users who cancelled with cancel_at_period_end retain access until the paid
 * period actually ends (matching the server-side resolver).
 */
export function calculateEffectiveTier(user: User | null | any): number {
    if (!user) return 0;

    // AUTHORITATIVE FIELD: 'tier' is the single source of truth
    const tier = user.tier || 0;

    // Admin/Super user has access to everything
    if (tier === 99 || tier === 3) return 3;

    // No subscription data at all → free tier
    if (tier === 0) return 0;

    // Date-first check — ignore the static status field
    if (user.subscription_end) {
        const now = new Date();
        const end = new Date(user.subscription_end);
        const grace = new Date(end);
        grace.setDate(grace.getDate() + 3);

        if (now <= grace) {
            return tier;
        }
        return 0; // Expired past grace period
    }

    // No end date but has tier → manual/admin grant (perpetually active)
    return tier;
}

/**
 * Convert trial record to TrialStatus
 * Supports both raw PocketBase record and unified API TrialData format
 */
export function parseTrialRecord(record: any): TrialStatus {
    // Unified API uses end_date/start_date, PocketBase uses expires_at/started_at
    const expiresAtStr = record.expires_at || record.end_date;
    const startedAtStr = record.started_at || record.start_date;

    const expiresAt = expiresAtStr ? new Date(expiresAtStr) : new Date(0);
    const startedAt = startedAtStr ? new Date(startedAtStr) : new Date(0);
    const now = new Date();

    if (isNaN(expiresAt.getTime()) && expiresAtStr) {
        console.warn(`⚠️ [parseTrialRecord] Invalid expiresAt for ${record.app_slug || record.appSlug}:`, { expiresAtStr });
    }

    // Calculate days left
    const daysLeft = Math.ceil((expiresAt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

    // Determine active status:
    // 1. If status is explicitly 'active' (from unified API)
    // 2. If it's explicitly marked active AND has not expired (PocketBase format)
    const active = record.status === 'active' || (record.active === true && expiresAt > now);

    return {
        id: record.id || '',
        appSlug: record.app_slug || record.appSlug,
        level: record.level,
        active: !!active,
        daysLeft: Math.max(0, daysLeft),
        expiresAt,
        startedAt,
    };
}

/**
 * Calculate app permission for a specific app
 */
export function calculateAppPermission(
    app: AppRecord,
    effectiveTier: number,
    trial?: TrialStatus
): AppPermission {
    const minTier = app.min_tier || 0;
    const hasTierAccess = effectiveTier >= minTier;
    const hasTrialAccess = trial?.active || false;
    const hasAccess = hasTierAccess || hasTrialAccess;

    let accessReason: AppPermission['accessReason'] = 'none';
    if (minTier === 0) {
        accessReason = 'free';
    } else if (hasTierAccess) {
        accessReason = 'subscription';
    } else if (hasTrialAccess) {
        accessReason = 'trial';
    }

    return {
        slug: app.slug,
        name: app.name,
        minTier,
        hasAccess,
        accessReason,
        trial: hasTrialAccess ? trial : undefined,
    };
}

/**
 * Get detailed app access status for UI display
 * Implements the complete authorization logic with badges, buttons, and expiration info
 */
export function getAppAccessStatus(
    app: AppRecord,
    effectiveTier: number,
    subscriptionEnd: Date | undefined,
    trial: TrialStatus | undefined,
    allTrials: TrialStatus[],
    activePromos: PromoData[] = [],
): import('./types').AppAccessStatus {
    const minTier = app.min_tier || 0;
    const now = new Date();

    // SCENARIO 0: Free tier (tier 0) apps are ALWAYS accessible to authenticated users
    if (minTier === 0) {
        return {
            slug: app.slug,
            name: app.name,
            minTier: 0,
            canAccess: true,
            badge: {
                type: 'free',
                color: 'green',
                text: 'Always Free',
            },
            subtext: null,
            button: null,
        };
    }

    // Real subscription = subscription end date exists and is in the future (+ 3-day grace)
    const hasRealSubscription = !!subscriptionEnd && (() => {
        const grace = new Date(subscriptionEnd);
        grace.setDate(grace.getDate() + 3);
        return now <= grace;
    })();

    // Check if subscription covers this app (must be a real active subscription, not just trial-elevated tier)
    const hasSubscriptionAccess = hasRealSubscription && effectiveTier >= minTier;

    // Check if user has active trial for this app
    const hasActiveTrialAccess = trial?.active || false;

    // Check if user has EVER had a trial for this app/level (active or expired)
    // This includes: exact app_slug match, level_N sentinel, or matching level field
    const hasUsedTrial = allTrials.some(t => 
        t.appSlug === app.slug || 
        t.appSlug === `level_${minTier}` || 
        t.level === minTier
    );

    // Calculate subscription days left (if applicable)
    let subscriptionDaysLeft = 0;
    if (subscriptionEnd && effectiveTier > 0) {
        subscriptionDaysLeft = Math.ceil((subscriptionEnd.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    }

    // SCENARIO 1: Subscription covers this app
    if (hasSubscriptionAccess) {
        return {
            slug: app.slug,
            name: app.name,
            minTier,
            canAccess: true,
            badge: {
                type: minTier === 0 ? 'free' : 'subscription',
                color: 'green',
                text: minTier === 0 ? 'Always Free' : 'Subscription Active',
            },
            subtext: minTier === 0
                ? null
                : subscriptionEnd
                    ? `Subscription ends in ${subscriptionDaysLeft} days`
                    : 'Subscription Active',
            button: null,
            subscription: subscriptionEnd ? {
                daysUntilExpiration: subscriptionDaysLeft,
                expiresAt: subscriptionEnd,
            } : undefined,
        };
    }

    // SCENARIO 1.5: Active platform-wide promotion for this app
    // Only shown to users who DON'T have subscription access (paid users see Scenario 1)
    const activePromo = activePromos.find(p => p.app_slug === app.slug) || null;

    if (activePromo) {
        const promoEnd = new Date(activePromo.end_date);
        const daysLeft = Math.max(0, Math.ceil((promoEnd.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));
        return {
            slug: app.slug,
            name: app.name,
            minTier,
            canAccess: true,
            badge: {
                type: 'promo',
                color: 'orange',
                text: activePromo.label,
            },
            subtext: `Free demo until ${promoEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`,
            button: null,
            promo: {
                id: activePromo.id,
                label: activePromo.label,
                endsAt: promoEnd,
                daysLeft,
            },
        };
    }

    // SCENARIO 2: User has active trial for this app (higher tier)
    if (hasActiveTrialAccess && trial) {
        return {
            slug: app.slug,
            name: app.name,
            minTier,
            canAccess: true,
            badge: {
                type: 'trial',
                color: 'blue',
                text: 'TRIAL ACTIVE',
            },
            subtext: `Trial ends in ${trial.daysLeft} days`,
            button: null,
            trial: {
                active: true,
                daysLeft: trial.daysLeft,
                expiresAt: trial.expiresAt,
            },
        };
    }

    // SCENARIO 3: User has NOT used trial for this app - offer trial
    if (!hasUsedTrial) {
        return {
            slug: app.slug,
            name: app.name,
            minTier,
            canAccess: false,
            badge: {
                type: null,
                color: null,
                text: null,
            },
            subtext: null,
            button: {
                text: 'Start 5-Day Trial',
                action: 'start_trial',
            },
        };
    }

    // SCENARIO 4: Trial expired or used - show upgrade
    return {
        slug: app.slug,
        name: app.name,
        minTier,
        canAccess: false,
        badge: {
            type: null,
            color: null,
            text: null,
        },
        subtext: null,
        button: {
            text: `Upgrade to Level ${minTier}`,
            action: 'upgrade',
            targetTier: minTier,
        },
    };
}

/**
 * Main function to calculate all user permissions
 * This is the single source of truth for permission logic
 */
export function calculatePermissions(
    user: User | null,
    trials: TrialRecord[],
    apps: AppRecord[],
    activePromos: PromoData[] = [],
): UserPermissions {
    if (!user) {
        // Return default permissions for unauthenticated users
        return createDefaultPermissions();
    }

    const effectiveTier = calculateEffectiveTier(user);

    // Parse trials
    const parsedTrials = trials.map(parseTrialRecord);
    const activeTrials = parsedTrials.filter(t => t.active);
    const trialsMap: Record<string, TrialStatus> = {};
    parsedTrials.forEach(trial => {
        trialsMap[trial.appSlug] = trial;
    });

    // Calculate app permissions
    const appsMap: Record<string, AppPermission> = {};
    apps.forEach(app => {
        // Use 3-step trial lookup (same as evaluateAppAccess)
        let trial = parsedTrials.find(t => t.appSlug === app.slug && t.active);
        if (!trial && app.min_tier > 0) {
            const sentinelSlug = `level_${app.min_tier}`;
            trial = parsedTrials.find(t => t.appSlug === sentinelSlug && t.active);
        }
        if (!trial && app.min_tier > 0) {
            trial = parsedTrials.find(t => t.level === app.min_tier && t.active);
        }
        appsMap[app.slug] = calculateAppPermission(app, effectiveTier, trial);
    });

    // Calculate feature flags based on tier
    const features = {
        exportData: effectiveTier >= 1,
        prioritySupport: effectiveTier >= 2,
        apiAccess: effectiveTier >= 1,
        notifications: true, // Available to all
    };

    // Greeks Intelligence specific permissions
    const weekliesTrial = parsedTrials.find(t => 
        (t.appSlug === 'greeks-weeklies' || t.appSlug === 'level_2' || t.level === 2) && t.active
    );
    const canAccessWeeklies = effectiveTier >= 2 || !!weekliesTrial;

    const odteTrial = parsedTrials.find(t => 
        (t.appSlug === 'greeks-0dte' || t.appSlug === 'level_1' || t.level === 1) && t.active
    );
    const canAccess0dte = effectiveTier >= 1 || !!odteTrial || true; // 0DTE is free for Basic tier

    return {
        tier: user.tier || 0,
        effectiveTier,
        subscriptionLevel: user.tier || 0,
        subscriptionEnd: user.subscription_end ? new Date(user.subscription_end) : undefined,

        trials: trialsMap,
        activeTrials,

        apps: appsMap,

        // Convenience methods
        canAccessApp: (appSlug: string) => {
            return appsMap[appSlug]?.hasAccess || false;
        },

        getAppPermission: (appSlug: string) => {
            return appsMap[appSlug] || null;
        },

        hasActiveTrial: (appSlug: string) => {
            // Use 3-step lookup for trial
            const app = apps.find(a => a.slug === appSlug);
            if (!app) return false;
            const trial = parsedTrials.find(t => t.appSlug === appSlug && t.active) ||
                (app.min_tier > 0 ? parsedTrials.find(t => t.appSlug === `level_${app.min_tier}` && t.active) : undefined) ||
                (app.min_tier > 0 ? parsedTrials.find(t => t.level === app.min_tier && t.active) : undefined);
            return !!trial;
        },

        getAppAccessStatus: (appSlug: string) => {
            const app = apps.find(a => a.slug === appSlug);
            if (!app) return null;

            // Use 3-step lookup for trial
            let trial = parsedTrials.find(t => t.appSlug === appSlug && t.active);
            if (!trial && app.min_tier > 0) {
                const sentinelSlug = `level_${app.min_tier}`;
                trial = parsedTrials.find(t => t.appSlug === sentinelSlug && t.active);
            }
            if (!trial && app.min_tier > 0) {
                trial = parsedTrials.find(t => t.level === app.min_tier && t.active);
            }
            return getAppAccessStatus(
                app,
                effectiveTier,
                user.subscription_end ? new Date(user.subscription_end) : undefined,
                trial,
                parsedTrials,
                activePromos,
            );
        },

        features,
        canAccessWeeklies,
        canAccess0dte,
    };
}

/**
 * Centralized function to evaluate access to an app
 * Receives the app slug, user, and all trials
 * Answers exactly what the user needs to know for UI display and gatekeeping
 */
export function evaluateAppAccess(
    app: AppRecord | { slug: string, min_tier?: number, name?: string },
    user: User | null | any,
    allTrials: TrialRecord[] | TrialStatus[],
    activePromos: PromoData[] = [],
): import('./types').AppAccessStatus {
    const effectiveTier = calculateEffectiveTier(user);


    // Parse trials correctly
    const parsedTrials = allTrials.map((t: any) => {
        if ('appSlug' in t) return t as TrialStatus; // Already parsed
        return parseTrialRecord(t as TrialRecord);
    });

    const minTier = (app as any).min_tier ?? 0;

    // 3-step trial check (legacy-to-level migration path):
    // Step 1: Exact app_slug match (legacy per-app trial)
    let trial = parsedTrials.find(t => t.appSlug === app.slug && t.active);
    
    // Step 2: Sentinel slug match (level_N)
    if (!trial && minTier > 0) {
        const sentinelSlug = `level_${minTier}`;
        trial = parsedTrials.find(t => t.appSlug === sentinelSlug && t.active);
    }
    
    // Step 3: Any active trial with matching level field
    if (!trial && minTier > 0) {
        trial = parsedTrials.find(t => t.level === minTier && t.active);
    }

    const subscriptionEnd = user?.subscription_end ? new Date(user.subscription_end) : undefined;

    const appRecord: AppRecord = {
        id: (app as any).id || '',
        slug: app.slug,
        name: (app as any).name || app.slug,
        min_tier: (app as any).min_tier ?? 0,
        description: (app as any).description || '',
        icon: (app as any).icon || '',
        url: (app as any).url || '',
        active: (app as any).active ?? true,
        color: (app as any).color || 'blue'
    };

    return getAppAccessStatus(
        appRecord,
        effectiveTier,
        subscriptionEnd,
        trial,
        parsedTrials,
        activePromos,
    );
}

/**
 * Create default permissions for unauthenticated users
 */
function createDefaultPermissions(): UserPermissions {
    return {
        tier: 0,
        effectiveTier: 0,
        subscriptionLevel: 0,
        subscriptionEnd: undefined,

        trials: {},
        activeTrials: [],

        apps: {},

        canAccessApp: () => false,
        getAppPermission: () => null,
        hasActiveTrial: () => false,
        getAppAccessStatus: () => null,

        features: {
            exportData: false,
            prioritySupport: false,
            apiAccess: false,
            notifications: false,
        },

        canAccessWeeklies: false,
        canAccess0dte: false,
    };
}
